<template>
  <div v-if="!isLoading" class="px-6">
    <div class="w-full">
      <div class="flex justify-between items-center pb-6">
        <div>
          <button
            @click="goBack"
            class="flex items-center leading-none text-zt-dark-400 mb-2"
          >
            <zt-icon name="angle-left" />
            {{ $t('message.documentDetail.buttonGoBack') }}
          </button>
          <h1 class="font-normal text-format text-3xl">
            {{ $t('message.documentDetail.title') }}
          </h1>
        </div>
        <div v-if="!isEmpty" class="flex">
          <div class="mr-4" v-if="false">
            <zt-button icon="file" outlined>
              {{ $t('message.documentDetail.buttonReport') }}
            </zt-button>
          </div>
          <div v-if="isActionAllowed(29) && isDownloadableFile">
            <zt-button
              icon="box-arrow-down"
              :disabled="isDownloadLoading"
              :loader="isDownloadLoading"
              @click="getDocumentFile"
            >
              {{ $t('message.documentDetail.buttonDownload') }}
            </zt-button>
          </div>
          <div
            v-if="isActionAllowed(29) && isPDFXML && isDownloadableFile"
            class="ml-4"
          >
            <zt-button
              icon="box-arrow-down"
              :disabled="isDownloadXMLLoading"
              :loader="isDownloadXMLLoading"
              @click="getDocumentXMLFile"
            >
              {{ $t('message.documentDetail.buttonDownloadXML') }}
            </zt-button>
          </div>
        </div>
      </div>
      <div v-if="isEmpty" class="h-dessert">
        <div class="flex justify-center items-center select-none">
          <div
            class="shadow rounded bg-white max-w-2xl flex-1 flex items-center"
          >
            <DesertMessage
              class="bg-white"
              :mainTitle="$t('message.documentDetail.emptyDocument.title')"
              :mainContent="$t('message.documentDetail.emptyDocument.content')"
            />
          </div>
        </div>
      </div>
      <div v-else class="cgap flex">
        <div class="w-1/2">
          <ViewDocument ref="document" />
        </div>
        <div class="cgap flex flex-col w-1/2">
          <ConfigDocument />
          <DocumentsInformation />
        </div>
      </div>
    </div>
  </div>
  <div class="w-full h-custom relative" v-else>
    <vue-element-loading
      :active="isLoading"
      spinner="spinner"
      duration="1.5"
      color="#4f4f4f"
      size="50"
    />
  </div>
</template>

<script>
import VueElementLoading from 'vue-element-loading'
import DocumentsInformation from '../components/DocumentsInformation/index'
import ConfigDocument from '../components/ConfigDocument/index'
import ViewDocument from '../components/ViewDocument/index'
import useDocumentDetail from '@/modules/Documents/detail/hooks/useDocumentDetail'
const {
  reset: resetDocumentDetail,
  setDocumentDetail,
  state: stateDetailStore
} = useDocumentDetail()
import Search from '@/apiClient/Search'
import User from '@/apiClient/User'
import DesertMessage from '@/components/generics/DesertMessage404.vue'
import { mapGetters } from 'vuex'
import { isEmpty } from 'lodash'

export default {
  name: 'DocumentDetail',
  provide() {
    return {
      setDocumentDetail,
      stateDetailStore
    }
  },
  components: {
    DesertMessage,
    VueElementLoading,
    DocumentsInformation,
    ConfigDocument,
    ViewDocument
  },
  data() {
    return {
      isLoading: false,
      isDownloadLoading: false,
      isDownloadXMLLoading: false,
      isEmpty: false
    }
  },
  computed: {
    ...mapGetters(['isActionAllowed']),
    applicationId() {
      return this.$store.state.user.aplicacion.id
    },
    isDownloadableFile() {
      return (
        !isEmpty(stateDetailStore.detail.dataFile.ext) &&
        !this.isDocumentCancelled
      )
    },
    isPDFXML() {
      return (
        !isEmpty(stateDetailStore.detail.dataFile.ext) &&
        stateDetailStore.detail.dataFile.typeDocument === 'pdfxml'
      )
    },
    isDocumentCancelled() {
      return stateDetailStore.detail.document.estado === 'ANULADO'
    }
  },
  async created() {
    this.searchApi = new Search()
    this.userApi = new User()
    await this.getDocumentDetail()
  },
  methods: {
    async getDocumentDetail() {
      this.isLoading = true
      try {
        const {
          data: { data }
        } = await this.searchApi.getDocumentDetail(this.$route.params.id)
        setDocumentDetail({
          idDocument: this.$route.params.id,
          ...data
        })
        this.isLoading = false
      } catch (error) {
        this.isEmpty = true
        this.isLoading = false
      }
    },
    async getDocumentFile() {
      if (isEmpty(stateDetailStore.detail.dataFile.ext)) {
        return
      }
      this.isDownloadLoading = true
      try {
        const { data: data } = await this.userApi.getDocumentPDFFILE(
          this.applicationId,
          stateDetailStore.detail.idDocument
        )
        await this.download(data)
      } catch (error) {
        console.error(error)
      } finally {
        this.isDownloadLoading = false
      }
    },
    async getDocumentXMLFile() {
      if (isEmpty(stateDetailStore.detail.dataFile.ext)) {
        return
      }
      this.isDownloadXMLLoading = true
      try {
        const { data: data } = await this.userApi.getDocumentPDFXML(
          this.applicationId,
          stateDetailStore.detail.idDocument
        )
        await this.download(data, true)
      } catch (error) {
        console.error(error)
      } finally {
        this.isDownloadXMLLoading = false
      }
    },
    download(data, xml = false) {
      const blob = new Blob([data], {
        type: xml ? 'application/xml' : 'application/pdf'
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      const fileName = this.getFileName(xml)
      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    getFileName(xml) {
      if (
        xml &&
        stateDetailStore.detail.dataFile.downloadName.endsWith('.pdf')
      ) {
        return stateDetailStore.detail.dataFile.downloadName.replace(
          '.pdf',
          '.xml'
        )
      }
      return stateDetailStore.detail.dataFile.downloadName
    },
    goBack() {
      this.hasHistory() ? this.$router.go(-1) : this.$router.push('/')
    },
    hasHistory() {
      return window.history.length > 2
    }
  },
  beforeDestroy() {
    resetDocumentDetail()
  }
}
</script>

<style scoped>
.h-dessert {
  height: calc(100vh - 145px);
}
.h-custom {
  height: calc(100vh - 122px);
}
.cgap {
  gap: 1.8rem;
}
.shadow {
  box-shadow: 0 1px 8px 0 rgba(0, 0, 0, 0.2), 0 3px 3px 0 rgba(0, 0, 0, 0.12),
    0 3px 4px 0 rgba(0, 0, 0, 0.14);
}
.text-format {
  font-family: Roboto, sans-serif;
  color: #707070;
}
</style>
